import type { Gender } from "@/lib/onboarding/onboarding-schemas";

/**
 * The profile fields the signup collects, as the backend keeps them.
 *
 * They were collected and thrown away for a long time, because neither the register call nor
 * `PATCH /auth/customers/me` took them — the request recorded as item 4 of
 * the backend team's queue. They delivered `gender` and `birthDate` on 2026-10-07
 * (`feature/customer-gender-birthdate`), and this is where the front meets that contract.
 *
 * **One definition for both directions and both callers.** The panel's profile adapter had this
 * pair first (`host-profile-adapter.ts`) and reads from here now: the onboarding writes it on
 * register, the panel writes it on update and reads it back, and a rule that lives twice is a
 * rule that drifts.
 *
 * `marketingOptIn` is still not accepted anywhere, so it is still collected and still dropped.
 * The API refuses unknown keys (`forbidNonWhitelisted`), so sending it would fail the whole
 * registration — it waits for its column.
 */

/** The backend's `enum Gender` (`schema.prisma`): three values. */
export const BACKEND_GENDERS = ["MALE", "FEMALE", "OTHER"] as const;
export type BackendGender = (typeof BACKEND_GENDERS)[number];

/**
 * The backend's three genders against the design's four.
 *
 * `customers.gender` is `MALE | FEMALE | OTHER` (PR #56); the onboarding asks Feminino,
 * Masculino, Outro and "Prefiro não informar" (`onboarding-schemas.ts`). So:
 *
 * - `NON_BINARY` ("Outro") writes as `OTHER` and reads back as `NON_BINARY`, a faithful pair;
 * - `UNDISCLOSED` ("Prefiro não informar") writes as **nothing**, because "prefer not to say" is
 *   the absence of a value, not a value — mapping it to `OTHER` would read back as "Outro" and
 *   put a word in the person's mouth.
 *
 * The cost of that second rule is a lossy round trip: whoever chooses "Prefiro não informar"
 * sees "Não informado" on the next read. Registered as a backend need (item 4): the column wants
 * a fourth value to tell "did not answer" from "chose not to answer".
 */
export const GENDER_TO_BACKEND: Record<Gender, BackendGender | null> = {
  MALE: "MALE",
  FEMALE: "FEMALE",
  NON_BINARY: "OTHER",
  UNDISCLOSED: null,
};

export const GENDER_FROM_BACKEND: Record<string, Gender> = {
  MALE: "MALE",
  FEMALE: "FEMALE",
  OTHER: "NON_BINARY",
};

/**
 * The gender on the way to **register**, where absence and `null` are not the same thing.
 *
 * `POST /auth/customers/register` creates the customer, so there is nothing to clear: the key is
 * left out of the body entirely. An update clears with `null` instead — that is the panel's
 * call, and it reads `GENDER_TO_BACKEND` directly.
 */
export function toBackendGender(gender: Gender | undefined): BackendGender | undefined {
  if (gender === undefined) return undefined;

  return GENDER_TO_BACKEND[gender] ?? undefined;
}

/**
 * The birth date on the way to the backend, which asks for `YYYY-MM-DD` (`@IsDateString()`).
 *
 * The form already holds it that way (`birthDateSchema`), so this only guards the empty string —
 * which is not a date and must not travel as one.
 */
export function toBackendBirthDate(birthDate: string | undefined): string | undefined {
  const value = birthDate?.trim() ?? "";

  return value === "" ? undefined : value;
}
